import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import useBodyScrollLock from '../../../shared/hooks/useBodyScrollLock';
import './BarcodeScanner.css';

// Suppress audio playback errors from html5-qrcode
const originalPlay = HTMLMediaElement.prototype.play;
HTMLMediaElement.prototype.play = function () {
  try {
    const promise = originalPlay.call(this);
    if (promise && typeof promise.catch === 'function') {
      promise.catch((error) => {
        const errorStr = error?.message || error?.toString() || '';
        if (!errorStr.includes('play()') && !errorStr.includes('media was removed')) {
          throw error;
        }
      });
    }
    return promise;
  } catch (error) {
    const errorStr = error?.message || error?.toString() || '';
    if (!errorStr.includes('play()') && !errorStr.includes('media was removed')) {
      throw error;
    }
    return Promise.reject(error);
  }
};

const SCAN_SUCCESS_DELAY_MS = 400;
const READER_ELEMENT_ID = 'qr-reader';

const getCameraError = (err) => {
  const msg = err?.message || err?.toString() || '';
  if (/NotAllowed|Permission/i.test(msg)) {
    return {
      title: 'Camera access denied',
      message: 'Allow camera access in your browser settings, then try again.',
    };
  }
  if (/NotFound|DevicesNotFound/i.test(msg)) {
    return {
      title: 'No camera found',
      message: 'Use a device with a camera to scan QR codes.',
    };
  }
  if (/secure|https/i.test(msg)) {
    return {
      title: 'Secure connection required',
      message: 'Camera access requires a secure (HTTPS) connection.',
    };
  }
  return {
    title: 'Camera unavailable',
    message: 'Failed to start the camera. Check permissions and try again.',
  };
};

const getQrBoxSize = () => {
  const isMobile = window.innerWidth < 768;
  if (isMobile) {
    return Math.min(window.innerWidth * 0.65, 280);
  }
  return 250;
};

const getReaderVideos = (elementId = READER_ELEMENT_ID) => {
  const container = document.getElementById(elementId);
  if (!container) return [];
  return Array.from(container.querySelectorAll('video'));
};

/**
 * html5-qrcode's RenderedCameraImpl throws on video abort/error:
 *   "RenderedCameraImpl video surface onabort() called"
 * Browsers can abort long-lived streams (idle/power/tab background). Replace
 * those handlers so CRA's overlay does not surface an uncaught runtime error.
 */
const neutralizeLibraryVideoHandlers = (onInterrupted) => {
  getReaderVideos().forEach((video) => {
    video.onabort = () => {
      onInterrupted?.('abort');
    };
    video.onerror = () => {
      onInterrupted?.('error');
    };
  });
};

/** Force-stop any lingering MediaStream tracks left on the reader video element. */
const releaseReaderMediaTracks = () => {
  getReaderVideos().forEach((video) => {
    // Prevent library throw handlers from firing while we tear down the stream.
    video.onabort = null;
    video.onerror = null;

    const stream = video.srcObject;
    if (stream && typeof stream.getTracks === 'function') {
      stream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch {
          // ignore
        }
      });
    }
    video.srcObject = null;
    try {
      video.pause();
    } catch {
      // ignore
    }
  });
};

function BarcodeScanner({
  onScan,
  onClose,
  title = 'Scan Device QR',
  subtitle = 'Align the QR code within the frame.',
  expectedDeviceId = null,
}) {
  const scannerRef = useRef(null);
  const isCleaningRef = useRef(false);
  const isScannerRunningRef = useRef(false);
  const [isStarting, setIsStarting] = useState(true);
  const [isScanning, setIsScanning] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [error, setError] = useState(null);

  useBodyScrollLock(true);

  const safeStopScanner = useCallback(async (scanner) => {
    if (!scanner) {
      releaseReaderMediaTracks();
      return;
    }

    try {
      const state =
        typeof scanner.getState === 'function' ? scanner.getState() : null;
      // Html5QrcodeScannerState: NOT_STARTED=1, SCANNING=2, PAUSED=3
      // Only stop when the camera is actually running.
      const isCameraActive =
        state === Html5QrcodeScannerState.SCANNING ||
        state === Html5QrcodeScannerState.PAUSED ||
        state === 2 ||
        state === 3;

      if (isCameraActive) {
        try {
          await scanner.stop();
        } catch (err) {
          if (!err?.message?.includes('Cannot stop')) {
            console.error('Error stopping scanner:', err);
          }
        }
      }

      if (typeof scanner.clear === 'function') {
        try {
          scanner.clear();
        } catch (err) {
          console.error('Error clearing scanner:', err);
        }
      }
    } catch (err) {
      console.error('Exception stopping scanner:', err);
    } finally {
      releaseReaderMediaTracks();
    }
  }, []);

  const handleClose = useCallback(async () => {
    if (isCleaningRef.current) {
      onClose();
      return;
    }

    isCleaningRef.current = true;
    setIsScanning(false);
    try {
      await safeStopScanner(scannerRef.current);
    } finally {
      isScannerRunningRef.current = false;
      scannerRef.current = null;
      onClose();
    }
  }, [onClose, safeStopScanner]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleClose]);

  useEffect(() => {
    let html5QrCode = null;
    let stopInProgress = false;
    let cancelled = false;

    const startScanner = async () => {
      try {
        isCleaningRef.current = false;
        stopInProgress = false;
        setIsStarting(true);
        setError(null);

        html5QrCode = new Html5Qrcode(READER_ELEMENT_ID, { verbose: false });
        scannerRef.current = html5QrCode;

        const frameSize = getQrBoxSize();
        const config = {
          fps: 15,
          qrbox: { width: frameSize, height: frameSize },
          disableFlip: false,
          aspectRatio: 1.0,
          disableBeep: true,
        };

        const qrCodeSuccessCallback = (decodedText) => {
          if (isCleaningRef.current || stopInProgress || cancelled) {
            return;
          }

          isCleaningRef.current = true;
          stopInProgress = true;
          setIsScanning(false);
          setScanSuccess(true);

          const finishScan = () => {
            if (!cancelled) {
              onScan(decodedText);
            }
          };

          safeStopScanner(html5QrCode).then(() => {
            isScannerRunningRef.current = false;
            scannerRef.current = null;
            stopInProgress = false;
            setTimeout(finishScan, SCAN_SUCCESS_DELAY_MS);
          });
        };

        await html5QrCode.start(
          { facingMode: 'environment' },
          config,
          qrCodeSuccessCallback,
          () => {}
        );

        if (cancelled || isCleaningRef.current) {
          await safeStopScanner(html5QrCode);
          isScannerRunningRef.current = false;
          scannerRef.current = null;
          return;
        }

        // Override html5-qrcode throw-on-abort handlers (long-lived streams).
        neutralizeLibraryVideoHandlers(() => {
          if (cancelled || isCleaningRef.current || stopInProgress) {
            return;
          }
          isCleaningRef.current = true;
          stopInProgress = true;
          setIsScanning(false);
          safeStopScanner(html5QrCode).then(() => {
            isScannerRunningRef.current = false;
            scannerRef.current = null;
            stopInProgress = false;
            if (!cancelled) {
              setError({
                title: 'Camera interrupted',
                message: 'The camera stopped unexpectedly. Close and try scanning again.',
              });
            }
          });
        });

        isScannerRunningRef.current = true;
        setIsStarting(false);
        setIsScanning(true);
      } catch (err) {
        if (!cancelled) {
          console.error('Error starting scanner:', err);
          setError(getCameraError(err));
          setIsStarting(false);
          setIsScanning(false);
        }
        isScannerRunningRef.current = false;
        isCleaningRef.current = false;
        stopInProgress = false;
        releaseReaderMediaTracks();
      }
    };

    startScanner();

    return () => {
      cancelled = true;
      isCleaningRef.current = true;
      const scanner = scannerRef.current;
      isScannerRunningRef.current = false;
      scannerRef.current = null;
      // Always attempt release on unmount (cancel, success, or parent hide).
      safeStopScanner(scanner);
    };
  }, [onScan, safeStopScanner]);

  return ReactDOM.createPortal(
    <div
      className="barcode-scanner-root"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <div className="barcode-scanner-backdrop" onClick={handleClose} aria-hidden="true" />

      <div className="barcode-scanner-shell" onClick={(e) => e.stopPropagation()}>
        <div className="barcode-scanner-stage">
          <div id="qr-reader" className="barcode-scanner-reader" />

          <div className="barcode-scanner-ui" aria-hidden={!!error}>
            <header className="barcode-scanner-header">
              <button
                type="button"
                className="barcode-scanner-close"
                onClick={handleClose}
                aria-label="Close scanner"
              >
                ✕
              </button>
              <h2 className="barcode-scanner-title">{title}</h2>
            </header>

            <div className="barcode-scanner-frame-wrap">
              <div className={`barcode-scanner-frame ${scanSuccess ? 'barcode-scanner-frame--success' : ''}`}>
                <span className="barcode-scanner-corner barcode-scanner-corner--tl" />
                <span className="barcode-scanner-corner barcode-scanner-corner--tr" />
                <span className="barcode-scanner-corner barcode-scanner-corner--bl" />
                <span className="barcode-scanner-corner barcode-scanner-corner--br" />
                {isScanning && !scanSuccess && <span className="barcode-scanner-scan-line" />}
              </div>
            </div>

            <div className="barcode-scanner-instructions">
              {scanSuccess ? (
                <>
                  <p className="barcode-scanner-instruction-title">Code detected</p>
                  <p className="barcode-scanner-instruction-subtitle">Verifying device…</p>
                </>
              ) : (
                <>
                  <p className="barcode-scanner-instruction-title">Point camera at the device</p>
                  <p className="barcode-scanner-instruction-subtitle">{subtitle}</p>
                  {expectedDeviceId && (
                    <p className="barcode-scanner-expected">
                      Expected: <strong>{expectedDeviceId}</strong>
                    </p>
                  )}
                  {isStarting && (
                    <p className="barcode-scanner-status">Starting camera…</p>
                  )}
                  {isScanning && (
                    <p className="barcode-scanner-status">Scanning…</p>
                  )}
                </>
              )}
            </div>
          </div>

          {scanSuccess && (
            <div className="barcode-scanner-success-flash" aria-live="polite">
              <span className="barcode-scanner-success-icon">✓</span>
            </div>
          )}
        </div>

        {error && (
          <div className="barcode-scanner-error-panel">
            <p className="barcode-scanner-error-title">{error.title}</p>
            <p className="barcode-scanner-error-message">{error.message}</p>
            <button type="button" className="barcode-scanner-error-close" onClick={handleClose}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default BarcodeScanner;
